/**
 * Progress aggregation utilities for combining metrics from multiple uploads
 * Aggregates every 500ms to prevent excessive state updates
 */

import type { UploadProgressMetrics, UploadJob } from '../types/s3';

/**
 * Configuration for progress aggregation
 */
export interface ProgressAggregationConfig {
  aggregationIntervalMs: number;
}

/**
 * Default progress aggregation - every 500ms
 */
export const DEFAULT_AGGREGATION_CONFIG: ProgressAggregationConfig = {
  aggregationIntervalMs: 500,
};

/**
 * Aggregate progress metrics from multiple jobs
 */
export const aggregateProgressMetrics = (
  jobs: Map<string, UploadJob>
): UploadProgressMetrics => {
  let totalFiles = 0;
  let filesUploaded = 0;
  let filesFailed = 0;
  let totalBytes = 0;
  let bytesUploaded = 0;
  let earliestStartTime: Date | null = null;
  const now = new Date();

  for (const job of jobs.values()) {
    // Skip cancelled jobs
    if (job.status === 'cancelled') continue;

    totalFiles += job.files.length;
    filesUploaded += job.files.filter(f => f.status === 'completed').length;
    filesFailed += job.files.filter(f => f.status === 'failed').length;

    totalBytes += job.files.reduce((sum, f) => sum + f.file.size, 0);
    bytesUploaded += job.files.reduce((sum, f) => sum + f.bytesUploaded, 0);

    // Track earliest start time
    if (
      !earliestStartTime ||
      job.progress.startTime.getTime() < earliestStartTime.getTime()
    ) {
      earliestStartTime = new Date(job.progress.startTime);
    }
  }

  const startTime = earliestStartTime || now;
  const elapsedMs = now.getTime() - startTime.getTime();
  const elapsedSeconds = Math.max(1, elapsedMs / 1000); // Prevent division by zero

  // Calculate average upload speed
  const uploadSpeed = Math.max(0, bytesUploaded / elapsedSeconds);

  // Estimate time remaining
  let estimatedTimeRemaining: number | null = null;
  if (uploadSpeed > 0 && bytesUploaded < totalBytes) {
    estimatedTimeRemaining = Math.ceil((totalBytes - bytesUploaded) / uploadSpeed);
  }

  const percentage =
    totalBytes > 0
      ? Math.min(100, Math.round((bytesUploaded / totalBytes) * 100))
      : 0;

  return {
    totalFiles,
    filesUploaded,
    filesFailed,
    totalBytes,
    bytesUploaded,
    uploadSpeed,
    estimatedTimeRemaining,
    percentage,
    startTime,
    currentTime: now,
  };
};

/**
 * Create a progress aggregator that batches updates at configured intervals
 */
export class ProgressAggregator {
  private intervalId: NodeJS.Timeout | null = null;
  private lastAggregatedTime = Date.now();
  private pendingAggregation = false;
  private config: ProgressAggregationConfig;

  constructor(
    private onProgressUpdate: (metrics: UploadProgressMetrics) => void,
    private getJobs: () => Map<string, UploadJob>,
    config: Partial<ProgressAggregationConfig> = {}
  ) {
    this.config = { ...DEFAULT_AGGREGATION_CONFIG, ...config };
  }

  /**
   * Schedule a progress aggregation update
   */
  scheduleUpdate(): void {
    if (this.pendingAggregation) {
      return; // Already scheduled
    }

    this.pendingAggregation = true;

    // Use requestAnimationFrame for smoother updates if available
    // Fall back to setTimeout if not in browser context
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => {
        this.executeUpdate();
      });
    } else {
      const timeSinceLastUpdate = Date.now() - this.lastAggregatedTime;
      const delayMs = Math.max(0, this.config.aggregationIntervalMs - timeSinceLastUpdate);
      
      setTimeout(() => {
        this.executeUpdate();
      }, delayMs);
    }
  }

  /**
   * Execute the aggregated progress update
   */
  private executeUpdate(): void {
    this.pendingAggregation = false;
    this.lastAggregatedTime = Date.now();

    const jobs = this.getJobs();
    const aggregatedMetrics = aggregateProgressMetrics(jobs);
    this.onProgressUpdate(aggregatedMetrics);
  }

  /**
   * Cleanup and stop aggregation
   */
  destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

/**
 * Merge individual job progress into overall metrics
 * Used when a single job's progress changes significantly
 */
export const mergeJobProgress = (
  currentMetrics: UploadProgressMetrics,
  job: UploadJob,
  allJobs: Map<string, UploadJob>
): UploadProgressMetrics => {
  // Recalculate from all jobs for consistency
  return aggregateProgressMetrics(allJobs);
};

/**
 * Check if progress has changed significantly enough to warrant an update
 */
export const hasSignificantProgressChange = (
  previous: UploadProgressMetrics,
  current: UploadProgressMetrics,
  thresholdPercent: number = 1
): boolean => {
  // Changed if percentage changed by threshold
  if (Math.abs(current.percentage - previous.percentage) >= thresholdPercent) {
    return true;
  }

  // Changed if files completed or failed changed
  if (
    current.filesUploaded !== previous.filesUploaded ||
    current.filesFailed !== previous.filesFailed
  ) {
    return true;
  }

  // Changed if estimated time changed significantly (more than 1 second)
  const currentEta = current.estimatedTimeRemaining ?? 0;
  const previousEta = previous.estimatedTimeRemaining ?? 0;
  if (Math.abs(currentEta - previousEta) > 1) {
    return true;
  }

  return false;
};
